/**
 * Service record rules that need no database: the next service due, the cost snapshot, and how a
 * record change moves stock - and, on the new store, FIFO per part (the engine's rules, for the page's preview).
 */

const pad = (n) => String(n).padStart(2, '0');

/** "yyyy-mm-dd" + months, as a date (end-of-month overflow as Date.setMonth does). */
const addMonths = (ymd, months) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ''));
    const base = m ? new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))) : null;
    if (!base) return null;
    base.setUTCMonth(base.getUTCMonth() + months);
    return `${base.getUTCFullYear()}-${pad(base.getUTCMonth() + 1)}-${pad(base.getUTCDate())}`;
};

const todayYmd = (now = new Date()) => `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

/**
 * Next service of a record: its KM + 10000 and its service date + 6 months (rules unchanged).
 * A new record used to count the 6 months from TODAY while an edited one counted from the
 * service date, so a back-dated service was due too late until someone edited it (audit FLT-11).
 */
export const nextServiceFor = (service, now = new Date()) => ({
    nextServiceKm: parseInt(service?.currentKm, 10) + 10000,
    nextServiceDate: addMonths(service?.date, 6) || addMonths(todayYmd(now), 6),
});

/** Cost of the parts at the prices they were taken at (each item keeps its unitCost). */
export const serviceCostOf = (items) => (items || [])
    .reduce((sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.unitCost) || 0), 0);

/**
 * The saved record for a new service. totalCost is kept as a snapshot: without it the cost was
 * worked out again from TODAY's inventory prices, so a price change rewrote old service costs and a
 * deleted part made them 0 (audit FLT-11).
 */
export const newServiceRecord = (service, referenceNumber, now = new Date()) => ({
    vehicleId: service.vehicleId,
    date: service.date,
    currentKm: service.currentKm,
    items: service.items,
    ...nextServiceFor(service, now),
    totalCost: Number(service.totalCost) || serviceCostOf(service.items),
    notes: service.notes,
    referenceNumber,
});

const qty = (item) => Math.abs(parseFloat(item?.quantity) || 0);

/**
 * Stock change per inventory item when a record's parts go from `oldItems` to `newItems`:
 * the old parts come back (+), the new ones are taken (-). Items that net to 0 are left out.
 * New record: oldItems = []; deleted record: newItems = [].
 */
export const stockDeltas = (oldItems, newItems) => {
    const deltas = new Map();
    const add = (id, amount) => { if (id) deltas.set(id, (deltas.get(id) || 0) + amount); };
    (oldItems || []).forEach((item) => add(item.itemId, qty(item)));
    (newItems || []).forEach((item) => add(item.itemId, -qty(item)));
    [...deltas.keys()].forEach((id) => { if (Math.abs(deltas.get(id)) < 1e-9) deltas.delete(id); });
    return deltas;
};

// ------------------------------------------------------------------------------------ FIFO per part (the new store)
// Owner, 28 Sep 2026: every service is costed by the purchase invoice its parts really went out with, OLDEST FIRST -
// not an average, and not whichever batch the employee clicks. On the new store (USE_CONTRACTS_V2) the page sends
// PARTS {name, partNumber, quantity} and the engine picks the batches. These are the engine's own rules
// (engine/api/fleet_records.py part_key, fifo_order, line_cost, plan_parts / fifo_take), so the page's preview shows
// what the engine will save; the engine's saved answer is the truth (stock can move between the two). The
// Firestore path above (serviceCostOf, newServiceRecord, stockDeltas) is unchanged.

export const UNNAMED = 'Unnamed Item';
const NO_DAY = '9999-12-31';                      // a batch with no invoice day and no entry time is taken last
const DUBAI_MIN = 4 * 60;                         // Dubai is UTC+4 all year (no summer time)

/** A typed text as the engine's _text reads it: trimmed (JavaScript's spaces); '' for nothing or an object. */
const textOf = (v) => (v === null || v === undefined || typeof v === 'object' ? '' : String(v).trim());

/**
 * A part: the Consolidated view's name (empty = Unnamed Item) and part number, trimmed, lower case - kept as a PAIR,
 * so "a_b" + "" and "a" + "b_" are never one part (engine part_key).
 */
export const partKeyOf = (name, partNumber) => [(textOf(name) || UNNAMED).toLowerCase(), textOf(partNumber).toLowerCase()];
/** A part key as a Map / object key. */
export const partKeyText = (key) => JSON.stringify(key);

/** Half-up to cents; exact for a quantity (3 decimals) x a unit cost (2 decimals), e.g. 4.5 x 15.75 = 70.88. */
export const round2 = (x) => Math.round(x * 100 + 1e-6) / 100;
/** A quantity to 0.001, as the stock columns keep it (numeric(14,3)). */
const round3 = (x) => Math.round(x * 1000 + 1e-7) / 1000;

const AMOUNT = /^[+-]?(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?$|^[+-]?\.\d+$/;
/** A number as the engine's parse_number reads it: a finite number, or plain digits (thousands commas allowed). */
export const amountOf = (v) => {
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    if (typeof v !== 'string') return null;
    const t = v.trim();
    return AMOUNT.test(t) ? Number(t.replace(/,/g, '')) : null;
};

/** A batch's quantity on the shelf as the engine reads it (parseFloat; not a number is 0), to 0.001. */
export const shelfOf = (batch) => {
    const v = batch?.quantity;
    const q = typeof v === 'number' ? v : parseFloat(v);
    return Number.isFinite(q) ? round3(q) : 0;
};

/** A saved line's quantity as a stock move reads it (stockDeltas: parseFloat, its size), to 0.001. */
const lineQtyOf = (line) => {
    const q = Math.abs(parseFloat(line?.quantity));
    return Number.isFinite(q) ? round3(q) : 0;
};

/** The page's short unit: Liter = L, Piece = Pcs, otherwise the unit. */
export const unitAbbrOf = (unit) => (unit === 'Liter' ? 'L' : (unit === 'Piece' ? 'Pcs' : (unit ? `${unit}` : '')));

/** A filter or a piece is taken one at a time (the quantity box is locked to 1, as the batch picker did). */
export const isQtyLockedPart = (part) => Boolean(part)
    && (String(part.name || '').toLowerCase().includes('filter') || part.unit === 'Piece');

// -- dates as the engine's read_moment / read_day read them (never guessed) -------------------------------------------
const YMD = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?)?\s*(Z|z|[+-]\d{2}:?\d{2})?$/;
const DMY = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})(?:[ T,]+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AaPp][Mm])?)?$/;
const PLAIN_DMY = /^\d{1,2}\/\d{1,2}\/\d{4}(?: \d{1,2}:\d{2})?$/;

/** Microseconds since 1970 of a wall time `offsetMin` ahead of UTC; null for a day or time that does not exist. */
const wallMicros = (y, mo, d, h, mi, s, micro, offsetMin) => {
    if (y < 1 || mo < 1 || mo > 12 || d < 1 || h > 23 || mi > 59 || s > 59) return null;
    const at = new Date(0);
    at.setUTCFullYear(y, mo - 1, d);
    if (at.getUTCFullYear() !== y || at.getUTCMonth() !== mo - 1 || at.getUTCDate() !== d) return null;
    at.setUTCHours(h, mi, s, 0);
    return at.getTime() * 1000 + micro - offsetMin * 60 * 1e6;
};

/** A zone's minutes ahead of UTC ("Z", "+04:00", "-0530"); no zone is Dubai's wall time; null when out of range. */
const zoneMinutes = (z) => {
    if (!z) return DUBAI_MIN;
    if (z === 'Z' || z === 'z') return 0;
    const digits = z.slice(1).replace(':', '');
    const total = Number(digits.slice(0, 2)) * 60 + Number(digits.slice(2, 4));
    if (total >= 24 * 60) return null;
    return z[0] === '-' ? -total : total;
};

/** undefined when `s` is in no form the app writes; null when it is one but not a real day / time. */
const readWritten = (s) => {
    let m = YMD.exec(s);
    if (m) {
        const offset = zoneMinutes(m[8]);
        if (offset === null) return null;
        const micro = m[7] ? Number(m[7].slice(0, 6).padEnd(6, '0')) : 0;
        return wallMicros(+m[1], +m[2], +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0), micro, offset);
    }
    m = DMY.exec(s);
    if (m) {
        let h = +(m[4] || 0);
        if (m[7]) {
            if (h < 1 || h > 12) return null;
            h = (h % 12) + (m[7].toLowerCase() === 'pm' ? 12 : 0);
        }
        return wallMicros(+m[3], +m[2], +m[1], h, +(m[5] || 0), +(m[6] || 0), 0, DUBAI_MIN);
    }
    return undefined;
};

/** A moment in microseconds since 1970, as the engine's read_moment: yyyy-mm-dd[ HH:MM[:SS[.f]]][zone] or
 *  dd/mm/yyyy[ HH:MM[:SS][ am/pm]] (a wall time without a zone is Dubai's; stray spaces around '/' removed). */
const momentOf = (v) => {
    if (typeof v !== 'string') return null;
    const t = v.trim();
    if (!t) return null;
    const at = readWritten(t);
    if (at !== undefined) return at;
    const tight = t.replace(/\s*\/\s*/g, '/');
    if (tight !== t && PLAIN_DMY.test(tight)) return readWritten(tight) ?? null;
    return null;
};

const dayOfMicros = (micros) => new Date(Math.floor(micros / 1000) + DUBAI_MIN * 60 * 1000).toISOString().slice(0, 10);

/** The Dubai day (yyyy-mm-dd) of a date or a time, as the engine's read_day reads it; null when it does not read. */
export const dubaiDayOf = (v) => {
    const at = momentOf(v);
    return at === null ? null : dayOfMicros(at);
};
/** A batch's invoice day: the form's yyyy-mm-dd, or dd/mm/yyyy (the forms read_day reads for this field). */
export const invoiceDayOf = dubaiDayOf;
/** yyyy-mm-dd as the owner reads a day: dd/mm/yyyy ('' for none). */
export const dayText = (ymd) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ''));
    return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
};

/**
 * Oldest first (engine fifo_order): the invoice day; a batch without one by the Dubai day it was entered (its own
 * created_at - only fields the page also has, so the preview sorts exactly like the engine); then the moment entered,
 * then the id. A batch with neither day goes last.
 */
export const fifoOrderOf = (batch) => {
    const at = momentOf(batch?.created_at);
    const day = invoiceDayOf(batch?.invoiceDate) || (at === null ? null : dayOfMicros(at)) || NO_DAY;
    return [day, at === null ? Infinity : at, String(batch?.id ?? '')];
};
export const compareFifo = (a, b) => {
    const x = fifoOrderOf(a);
    const y = fifoOrderOf(b);
    for (let i = 0; i < x.length; i += 1) {
        if (x[i] < y[i]) return -1;
        if (x[i] > y[i]) return 1;
    }
    return 0;
};

/** One line of a service: its quantity x its own unit cost, half-up to cents (an invoice line; engine line_cost). */
export const lineCostOf = (qty, unitCost) => round2((Number(qty) || 0) * (Number(unitCost) || 0));

/**
 * The cost of a record's lines: the sum of each line's own quantity x own unit cost (rounded per line, so the lines
 * add up to it); null when there is no line or a line has no quantity or unit cost of its own - unknown, never
 * priced at today's inventory price (audit FLT-11; engine parts_cost).
 */
export const linesCostOf = (items) => {
    if (!Array.isArray(items) || items.length === 0) return null;
    let total = 0;
    for (const line of items) {
        const q = amountOf(line?.quantity);
        const c = amountOf(line?.unitCost);
        if (q === null || c === null) return null;
        total += lineCostOf(q, c);
    }
    return round2(total);
};

const isNanText = (v) => (typeof v === 'number' && Number.isNaN(v)) || (typeof v === 'string' && v.trim().toLowerCase() === 'nan');

/**
 * What a service record cost - one rule for the page, the CSV and the engine's column (owner, 28 Sep 2026; engine
 * _Probe.total_cost): its kept total when that is a number above 0; a missing, NaN, 0 or negative one is the sum of
 * its lines at their OWN prices (a record with no line at all and a total of 0 cost 0); null - unknown, shown "—" -
 * when a line has no price of its own or the kept total is text that is no number. Never today's inventory price
 * (the page used to reprice such records at the batch's CURRENT price, audit FLT-11).
 */
export const recordCostOf = (record) => {
    const raw = record?.totalCost;
    const read = amountOf(raw);
    const kept = read === null ? null : round2(read);
    if (kept !== null && kept > 0) return kept;
    if (kept === null && !(raw === undefined || raw === null || raw === '' || isNanText(raw))) return null;
    const items = Array.isArray(record?.items) ? record.items : [];
    if (items.length === 0) return kept === 0 ? 0 : null;
    return linesCostOf(items);
};

/**
 * A saved line's part: its own name / part number (a line the engine made), else its batch's in `batchesById` (a
 * legacy line: {itemId, quantity, unitCost, invoiceNumber}); null when neither is known - such a line is kept as
 * saved, never moved (the engine names a legacy line only through a LIVE batch too).
 */
export const lineKeyOf = (line, batchesById) => {
    if (line && typeof line === 'object' && 'name' in line) return partKeyOf(line.name, line.partNumber);
    const batch = batchesById.get(String(line?.itemId ?? ''));
    return batch ? partKeyOf(batch.name, batch.partNumber) : null;
};

const byIdOf = (inventory) => new Map((inventory || []).filter(Boolean).map((b) => [String(b.id), b]));
const batchPartNumber = (batch) => textOf(batch?.partNumber || batch?.PartNumber || batch?.part_number || batch?.['Part Number']);

/**
 * A record's lines grouped per part, for the records table, the print and the CSV: {key, name, partNumber, keyPartNumber,
 * unit, quantity (summed), lines, known}. Name, part number and unit come from the line first, then its batch; a line
 * whose part is not known at all is its own group (known: false, name = its itemId). `partNumber` is for showing (a
 * batch's number under another field name too); `keyPartNumber` is the part number the part KEY reads (partNumber
 * only - partKeyOf, the engine's part_key): what a part sent back to the engine must carry (review 28 Sep 2026).
 */
export const partGroupsOf = (items, inventory) => {
    const byId = inventory instanceof Map ? inventory : byIdOf(inventory);
    const groups = new Map();
    (items || []).forEach((line, i) => {
        const key = lineKeyOf(line, byId);
        const k = key ? partKeyText(key) : `unknown:${i}`;
        const batch = byId.get(String(line?.itemId ?? ''));
        if (!groups.has(k)) {
            const own = Boolean(line) && typeof line === 'object' && 'name' in line;
            groups.set(k, {
                key,
                known: Boolean(key),
                name: own ? (textOf(line.name) || UNNAMED) : (batch ? (textOf(batch.name) || UNNAMED) : String(line?.itemId ?? '')),
                partNumber: own ? textOf(line.partNumber) : batchPartNumber(batch),
                keyPartNumber: own ? textOf(line.partNumber) : textOf(batch?.partNumber),
                unit: (own && textOf(line.unit)) || textOf(batch?.unit),
                quantity: 0,
                lines: [],
            });
        }
        const g = groups.get(k);
        g.quantity = round3(g.quantity + (amountOf(line?.quantity) ?? 0));
        g.lines.push(line);
    });
    return [...groups.values()];
};

const priceText = (v) => {
    const c = amountOf(v);
    return c === null ? '—' : c.toFixed(2);
};

/** "INV-1001 120 @ 15.75 · INV-1042 10 @ 18.90": where a part's quantity came from (the records table). */
export const invoicesLineOf = (group) => group.lines
    .map((line) => `${textOf(line?.invoiceNumber) || 'No invoice'} ${amountOf(line?.quantity) ?? 0} @ ${priceText(line?.unitCost)}`)
    .join(' · ');

/** "INV-1001: 120L @ 15.75; INV-1042: 10L @ 18.90": the CSV's Invoices Used for one part. */
export const invoicesCsvOf = (group) => group.lines
    .map((line) => `${textOf(line?.invoiceNumber) || 'No invoice'}: ${amountOf(line?.quantity) ?? 0}${unitAbbrOf(group.unit)} @ ${priceText(line?.unitCost)}`)
    .join('; ');

/** "Engine Oil 5W-30: (130L) (OIL-5W30)": one part of a record, as the records table / print / CSV wrote a line. */
export const partGroupText = (group) => `${group.known ? group.name : `${group.name} (Not Found)`}: (${group.quantity}${unitAbbrOf(group.unit)})${group.partNumber ? ` (${group.partNumber})` : ''}`;

/**
 * The parts on the shelf, for the service form: one entry per part {key, name, partNumber, unit, available (the sum
 * of its batches' shelves above 0), next (its oldest batch with stock), batches (those, oldest first)}.
 */
export const partsOf = (inventory) => {
    const parts = new Map();
    (inventory || []).filter(Boolean).forEach((batch) => {
        const key = partKeyOf(batch.name, batch.partNumber);
        const k = partKeyText(key);
        if (!parts.has(k)) parts.set(k, { key, available: 0, batches: [], first: batch });
        const shelf = shelfOf(batch);
        if (shelf > 0) {
            const p = parts.get(k);
            p.available = round3(p.available + shelf);
            p.batches.push(batch);
        }
    });
    return [...parts.values()].map(({ key, available, batches, first }) => {
        batches.sort(compareFifo);
        const shown = batches[0] || first;
        return {
            key,
            name: textOf(shown.name) || UNNAMED,
            partNumber: textOf(shown.partNumber),
            unit: textOf(shown.unit),
            available,
            next: batches[0] || null,
            batches,
        };
    });
};

/** The inventory as it stands once `savedItems` (an edited record's own lines) went back to their batches: what the
 *  record may take when a part changes (its own parts go back first). */
export const withReturned = (inventory, savedItems) => {
    const back = new Map();
    (savedItems || []).forEach((line) => {
        const id = String(line?.itemId ?? '');
        if (id) back.set(id, round3((back.get(id) || 0) + lineQtyOf(line)));
    });
    return (inventory || []).filter(Boolean).map((b) => (back.has(String(b.id))
        ? { ...b, quantity: round3(shelfOf(b) + back.get(String(b.id))) } : b));
};

/** A quantity the engine takes: a number above 0 with at most 3 decimals (numeric(14,3)); null otherwise. */
export const partQtyOf = (v) => {
    const q = amountOf(typeof v === 'number' ? v : String(v ?? ''));
    if (q === null || !(q > 0) || Math.abs(q * 1000 - Math.round(q * 1000)) > 1e-6) return null;
    return round3(q);
};

/**
 * The parts asked for, as the engine merges them: Map partKeyText -> {key, name, partNumber, quantity}, the same part
 * summed, in the order first asked. A quantity the engine would refuse is left out here (it refuses it on the save).
 */
export const wantedParts = (parts) => {
    const out = new Map();
    (parts || []).forEach((p) => {
        const q = partQtyOf(p?.quantity);
        if (q === null) return;
        const key = partKeyOf(p.name, p.partNumber);
        const k = partKeyText(key);
        const prev = out.get(k);
        out.set(k, prev
            ? { ...prev, quantity: round3(prev.quantity + q) }
            : { key, name: textOf(p.name) || UNNAMED, partNumber: textOf(p.partNumber), quantity: q });
    });
    return out;
};

/**
 * Which saved lines stay and what moves on a save (engine plan_parts). `wanted` null (parts not sent): every line
 * stays, nothing moves. A part whose saved quantity equals the wanted one keeps its lines - a notes / KM edit never
 * reprices a record, even after an older invoice was entered late (owner, 28 Sep 2026). A changed or removed part
 * gives every line back to its own batch and, when still wanted, is taken again oldest first; a new part is taken; a
 * line whose part is not known is always kept. Returns {kept: Map k -> lines, unknown: lines, returns: Map batch id
 * -> quantity, takes: Map k -> wanted part}.
 */
export const planParts = (savedItems, keyOfLine, wanted) => {
    const groups = new Map();
    const unknown = [];
    (savedItems || []).forEach((line) => {
        const key = keyOfLine(line);
        if (!key) {
            unknown.push(line);
            return;
        }
        const k = partKeyText(key);
        if (!groups.has(k)) groups.set(k, { lines: [], quantity: 0 });
        const g = groups.get(k);
        g.lines.push(line);
        g.quantity = round3(g.quantity + lineQtyOf(line));
    });
    const kept = new Map();
    const returns = new Map();
    const takes = new Map();
    groups.forEach((g, k) => {
        const want = wanted ? wanted.get(k) : null;
        if (!wanted || (want && want.quantity === g.quantity)) {
            kept.set(k, g.lines);
            return;
        }
        g.lines.forEach((line) => {
            const id = String(line?.itemId ?? '');
            if (id) returns.set(id, round3((returns.get(id) || 0) + lineQtyOf(line)));
        });
    });
    if (wanted) wanted.forEach((want, k) => { if (!kept.has(k)) takes.set(k, want); });
    return { kept, unknown, returns, takes };
};

const partLabel = (name, partNumber) => (partNumber ? `${name} (${partNumber})` : name);

/**
 * Take one part's quantity from its batches (oldest first, shelf above 0), splitting across them: one line per batch
 * taken (engine fifo_take). {lines} | {short} (the batches hold less) | {problem} (mixed units, a batch without a
 * unit cost). `shelf`: Map batch id -> quantity on the shelf.
 */
const takeOldestFirst = (batches, shelf, want) => {
    const label = partLabel(want.name, want.partNumber);
    const unit = textOf(batches[0]?.unit);
    const available = round3(batches.reduce((sum, b) => sum + shelf.get(String(b.id)), 0));
    if (available < want.quantity) {
        return { short: { key: want.key, name: want.name, partNumber: want.partNumber, unit, available, asked: want.quantity } };
    }
    const lines = [];
    let left = want.quantity;
    for (const b of batches) {
        if (left <= 0) break;
        const id = String(b.id);
        const take = round3(Math.min(shelf.get(id), left));
        const unitCost = amountOf(b.unitCost);
        if (unitCost === null) {
            return { problem: { key: want.key, message: `${label}, invoice ${textOf(b.invoiceNumber) || id} has no unit cost - set it on the Invoices view first.` } };
        }
        lines.push({
            itemId: id,
            name: textOf(b.name),
            partNumber: textOf(b.partNumber),
            unit: textOf(b.unit),
            invoiceNumber: textOf(b.invoiceNumber),
            invoiceDate: textOf(b.invoiceDate),
            quantity: take,
            unitCost,
            lineCost: lineCostOf(take, unitCost),
        });
        left = round3(left - take);
    }
    const units = new Set(lines.map((l) => l.unit.toLowerCase()));
    if (units.size > 1) {
        const shown = [...new Set(lines.map((l) => l.unit || 'no unit'))].join(', ');
        return { problem: { key: want.key, message: `${label}: the invoices it would be taken from are in different units (${shown}) - correct the unit on the Invoices view first.` } };
    }
    return { lines };
};

/**
 * What the engine would save for `parts` from the stock as loaded (the service form's preview; engine _take_parts).
 * `savedItems`: an edited record's own lines (they go back first when their part changes). {items (the lines as the
 * engine would save them), total (null when unknown), moved (stock would move), short: [{key, name, partNumber, unit,
 * available, asked}], problems: [{key, message}]}.
 */
export const fifoPreview = (inventory, parts, savedItems = []) => {
    const live = (inventory || []).filter(Boolean);
    const byId = byIdOf(live);
    const wanted = parts === undefined || parts === null ? null : wantedParts(parts);
    if (!wanted) {
        return { items: [...(savedItems || [])], total: linesCostOf(savedItems), moved: false, short: [], problems: [] };
    }
    const { kept, unknown, returns, takes } = planParts(savedItems, (line) => lineKeyOf(line, byId), wanted);
    const shelf = new Map(live.map((b) => [String(b.id), shelfOf(b)]));
    returns.forEach((q, id) => { if (shelf.has(id)) shelf.set(id, round3(shelf.get(id) + q)); });
    const items = [];
    const short = [];
    const problems = [];
    wanted.forEach((want, k) => {
        if (kept.has(k)) {
            items.push(...kept.get(k));
            return;
        }
        const batches = live
            .filter((b) => partKeyText(partKeyOf(b.name, b.partNumber)) === k && shelf.get(String(b.id)) > 0)
            .sort(compareFifo);
        const res = takeOldestFirst(batches, shelf, want);
        if (res.short) short.push(res.short);
        else if (res.problem) problems.push(res.problem);
        else {
            res.lines.forEach((l) => shelf.set(l.itemId, round3(shelf.get(l.itemId) - l.quantity)));
            items.push(...res.lines);
        }
    });
    items.push(...unknown);
    return { items, total: linesCostOf(items), moved: returns.size > 0 || takes.size > 0, short, problems };
};

/**
 * An edited record's parts for the service form: the lines grouped per known part ({name, partNumber, quantity}), the
 * lines whose part is not known (shown read-only; the engine keeps them as saved) and `lineParts` - the part key each
 * saved line was counted under (null = not known), in the lines' order. An edit sends `lineParts` as savedLineParts:
 * the engine refuses the save (409 stock-changed) when it names the old lines otherwise now, e.g. an invoice deleted
 * after this page loaded its copy of the inventory (review 28 Sep 2026). A part carries the part number its key reads
 * (keyPartNumber), never a batch's number under another field name - that would be another part to the engine.
 */
export const partsFromItems = (items, inventory) => {
    const byId = inventory instanceof Map ? inventory : byIdOf(inventory);
    const groups = partGroupsOf(items, byId);
    return {
        parts: groups.filter((g) => g.known).map((g) => ({ name: g.name, partNumber: g.keyPartNumber, unit: g.unit, quantity: g.quantity })),
        unknown: groups.filter((g) => !g.known).flatMap((g) => g.lines),
        lineParts: (items || []).map((line) => lineKeyOf(line, byId)),
    };
};

/** Two part lists ask for the same (the same parts, the same quantities) - an edit then sends no parts at all. */
export const sameParts = (a, b) => {
    const x = wantedParts(a);
    const y = wantedParts(b);
    if (x.size !== y.size) return false;
    return [...x.entries()].every(([k, p]) => y.has(k) && y.get(k).quantity === p.quantity);
};

/**
 * The store's save request (USE_CONTRACTS_V2): the service's fields and its PARTS - no items, no total, no reference
 * number (the engine picks the invoices oldest first, writes the lines and the total, and issues the number).
 * `parts` is left out when the form did not change them (the saved lines then stay exactly as they are). An edit's
 * parts go with `savedLineParts` (partsFromItems lineParts): how the form counted the record's old lines.
 */
export const newServiceRequest = (service, now = new Date()) => ({
    vehicleId: service.vehicleId,
    date: service.date,
    currentKm: service.currentKm,
    notes: service.notes,
    ...(Array.isArray(service.parts) ? { parts: service.parts.map((p) => ({ name: p.name, partNumber: p.partNumber, quantity: p.quantity })) } : {}),
    ...(Array.isArray(service.parts) && Array.isArray(service.savedLineParts) ? { savedLineParts: service.savedLineParts } : {}),
    ...nextServiceFor(service, now),
});

const MONEY_TEXT = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** 2079 -> "2,079.00". */
export const moneyText = (n) => MONEY_TEXT.format(n);

/**
 * What the page says after a save: what the engine SAVED ("Saved YELO-26/09/0012 - 2,079.00 AED (Engine Oil 5W-30
 * from 2 invoices)."), and "(stock changed since the preview)" when its total is not the preview's.
 */
export const savedServiceMessage = (record, previewTotal) => {
    if (!record) return 'Service saved.';
    const cost = recordCostOf(record);
    const split = partGroupsOf(record.items, [])
        .filter((g) => g.lines.length > 1)
        .map((g) => `${g.name} from ${g.lines.length} invoices`);
    const changed = typeof previewTotal === 'number' && cost !== null && Math.abs(cost - previewTotal) >= 0.005;
    return `Saved ${record.referenceNumber || 'the service'} - ${cost === null ? 'cost unknown' : `${moneyText(cost)} AED`}`
        + `${split.length ? ` (${split.join('; ')})` : ''}${changed ? ' (stock changed since the preview)' : ''}.`;
};
